import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzVpbDataService, CorporateTaxEntity,
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormControl, FormGroup} from "@angular/forms";
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {debounceTime, filter, merge, Observable, shareReplay, tap} from "rxjs";
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-algemeen',
  templateUrl: './algemeen.component.html',
  styleUrls: ['./algemeen.component.scss'],
  imports: [
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    CommonModule
  ]
})
export class AlgemeenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef= inject(DestroyRef);

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      tap(data => {
        this.algemeenFg.patchValue(data.aangifte?.algemeneVragen ?? {}, { emitEvent: false });
        this.agrarischFg.patchValue(data.aangifte?.balansEnResultatenrekening ?? {}, { emitEvent: false });
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({ bufferSize: 1, refCount: true })
    );

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: "Ja"},
    {value: false, label: "Nee"},
  ];

  public algemeenFg = new FormGroup({
    verzoekExplicietStandpunt: new FormControl(false, { nonNullable: true }),
    toelichtingAangifte: new FormControl<string | null>(null),
    regelingFunctioneleValuta: new FormControl(false, { nonNullable: true }),
    gebruikInnovatiebox: new FormControl(false, { nonNullable: true }),
    internationaleHoustervennootschap: new FormControl(false, { nonNullable: true }),
    toepassingBesluitBeleggingsinstellingen: new FormControl(false, { nonNullable: true }),
    toepassingBesluitWinstbepalingVerzekeraars: new FormControl(false, { nonNullable: true }),
    activiteitenOndernemingGewijzigd: new FormControl(false, { nonNullable: true }),
    wijzigingWaarderingsstelsel: new FormControl(false, { nonNullable: true }),
    willekeurigAfschijven: new FormControl(false, { nonNullable: true }),
    nieuweOfGewijzigdePensioenregeling: new FormControl(false, { nonNullable: true }),
    pensioenaanspraakVerminderd: new FormControl(false, { nonNullable: true }),
    vervreemdingAfgewaardeerdeVordering: new FormControl(false, { nonNullable: true }),
    vervreemdeAandelenMetFiscaleClaim: new FormControl(false, { nonNullable: true }),
    aftrekValutaverliesOpDeelnemingen: new FormControl(false, { nonNullable: true }),
    verdelingAandelenkapitaalGewijzigd: new FormControl(false, { nonNullable: true }),
    beperkingVerliesverrekeningInOverdrachtsjaar: new FormControl(false, { nonNullable: true }),
    earnOutRegeling: new FormControl(false, { nonNullable: true }),
    toepassingTegenbewijsRenteaftrek: new FormControl(false, { nonNullable: true }),
    schulden10aBuitenFiscaleEenheid: new FormControl(false, { nonNullable: true }),
    compenserendeHeffing10aSchulden: new FormControl(false, { nonNullable: true }),
    kennisCompenserende10aHeffingOntvanger: new FormControl(false, { nonNullable: true }),
    kwalificatieBelastbareWinst: new FormControl(false, { nonNullable: true }),
    saldoVorderingenEnSchuldenVerbondenLichamen: new FormControl<number | null>(null),
    rechtshandelingenMetBuitenlandseAandeelhouders: new FormControl(false, { nonNullable: true }),
    betaaldBedragVoorActiva: new FormControl<number | null>(null),
    lokaalDossierBedrijfsreorganisatieOfAfdrachten: new FormControl(false, { nonNullable: true }),
    belangInFiscaalTransparanteEntiteit: new FormControl(false, { nonNullable: true }),
    vermogenAfgezonderdInApv: new FormControl(false, { nonNullable: true }),
  })

  public agrarischFg = new FormGroup({
    agrarischeActiviteiten: new FormControl(false, { nonNullable: true }),
  })

  ngOnInit(): void {
    merge(
      this.algemeenFg.valueChanges,
      this.agrarischFg.valueChanges
    ).pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.saveForm();
    });
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  saveForm(): void {
    this.agrarischFg.updateValueAndValidity();
    this.algemeenFg.updateValueAndValidity();

    const algemeen = this.algemeenFg.getRawValue();

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          algemeneVragen: {
            ...currentEntity.aangifte?.algemeneVragen,
            verzoekExplicietStandpunt: algemeen.verzoekExplicietStandpunt ?? false,
            toelichtingAangifte: algemeen.toelichtingAangifte ?? undefined,
            regelingFunctioneleValuta: algemeen.regelingFunctioneleValuta ?? false,
            gebruikInnovatiebox: algemeen.gebruikInnovatiebox ?? false,
            internationaleHoustervennootschap: algemeen.internationaleHoustervennootschap ?? false,
            toepassingBesluitBeleggingsinstellingen: algemeen.toepassingBesluitBeleggingsinstellingen ?? false,
            toepassingBesluitWinstbepalingVerzekeraars: algemeen.toepassingBesluitWinstbepalingVerzekeraars ?? false,
            activiteitenOndernemingGewijzigd: algemeen.activiteitenOndernemingGewijzigd ?? false,
            wijzigingWaarderingsstelsel: algemeen.wijzigingWaarderingsstelsel ?? false,
            willekeurigAfschijven: algemeen.willekeurigAfschijven ?? false,
            nieuweOfGewijzigdePensioenregeling: algemeen.nieuweOfGewijzigdePensioenregeling ?? false,
            pensioenaanspraakVerminderd: algemeen.pensioenaanspraakVerminderd ?? false,
            vervreemdingAfgewaardeerdeVordering: algemeen.vervreemdingAfgewaardeerdeVordering ?? false,
            vervreemdeAandelenMetFiscaleClaim: algemeen.vervreemdeAandelenMetFiscaleClaim ?? false,
            aftrekValutaverliesOpDeelnemingen: algemeen.aftrekValutaverliesOpDeelnemingen ?? false,
            verdelingAandelenkapitaalGewijzigd: algemeen.verdelingAandelenkapitaalGewijzigd ?? false,
            beperkingVerliesverrekeningInOverdrachtsjaar: algemeen.beperkingVerliesverrekeningInOverdrachtsjaar ?? false,
            earnOutRegeling: algemeen.earnOutRegeling ?? false,
            toepassingTegenbewijsRenteaftrek: algemeen.toepassingTegenbewijsRenteaftrek ?? false,
            schulden10aBuitenFiscaleEenheid: algemeen.schulden10aBuitenFiscaleEenheid ?? false,
            compenserendeHeffing10aSchulden: algemeen.compenserendeHeffing10aSchulden ?? false,
            kennisCompenserende10aHeffingOntvanger: algemeen.kennisCompenserende10aHeffingOntvanger ?? false,
            kwalificatieBelastbareWinst: algemeen.kwalificatieBelastbareWinst ?? false,
            saldoVorderingenEnSchuldenVerbondenLichamen: algemeen.saldoVorderingenEnSchuldenVerbondenLichamen ?? 0,
            rechtshandelingenMetBuitenlandseAandeelhouders: algemeen.rechtshandelingenMetBuitenlandseAandeelhouders ?? false,
            betaaldBedragVoorActiva: algemeen.betaaldBedragVoorActiva ?? 0,
            lokaalDossierBedrijfsreorganisatieOfAfdrachten: algemeen.lokaalDossierBedrijfsreorganisatieOfAfdrachten ?? false,
            belangInFiscaalTransparanteEntiteit: algemeen.belangInFiscaalTransparanteEntiteit ?? false,
            vermogenAfgezonderdInApv: algemeen.vermogenAfgezonderdInApv ?? false,
          },
          balansEnResultatenrekening: {
            ...currentEntity.aangifte.balansEnResultatenrekening,
            agrarischeActiviteiten:
            this.agrarischFg.getRawValue().agrarischeActiviteiten,
          },
        },
      }
    })
  }
}
